import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  CloudUpload,
  GetApp,
  Description,
  Search,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import SuccessModal from './SuccessModal';
import { usePermissions } from '../hooks/usePermissions';

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
});

const StyledTableHead = styled(TableHead)({
  backgroundColor: '#f8fafc',
  '& th': {
    fontWeight: 600,
    color: '#475569',
    fontSize: '0.875rem',
  },
});

const PrimaryButton = styled(Button)({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)',
  },
});

const RegistrosSanitarios = () => {
  // Hook de permisos
  const { canWrite, canDelete } = usePermissions();

  const [registros, setRegistros] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRegistro, setSelectedRegistro] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    title: '',
    message: '',
    type: 'create'
  });
  const [formData, setFormData] = useState({
    regNo: '',
    fechaVencimiento: '',
    nombre: '',
    principiosActivos: '',
    laboratorioFabricante: '',
    para: '',
    paisOrigen: '',
    distribuidoPor: '',
    farmaceuticoResponsable: '',
    emailFarmaceutico: '',
    formaFarmaceutica: '',
    clasificacion: '',
    viaAdministracion: '',
    presentacion: '',
    vidaUtil: '',
    productoControlado: false,
    enRenovacion: false,
    codigoSAP: '',
  });

  useEffect(() => {
    loadRegistros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchTerm]);

  const loadRegistros = async () => {
    try {
      const token = localStorage.getItem('token');

      console.log('🔍 Cargando registros sanitarios...');
      console.log('🔑 Token:', token ? token.substring(0, 30) + '...' : 'NO TOKEN');

      let url = `/api/registros-sanitarios?page=${page}&limit=${limit}`;
      if (searchTerm && searchTerm.trim() !== '') {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Registros cargados:', data);
        setRegistros(data.registros || []);
        setPagination(data.pagination || { total: 0, pages: 0 });
      } else {
        console.error('❌ Error en respuesta:', response.status, response.statusText);
        const errorData = await response.json();
        console.error('Detalle del error:', errorData);

        // Si el token es inválido o expirado, redirigir al login
        if (errorData.code === 'TOKEN_EXPIRED' || errorData.code === 'INVALID_TOKEN' || errorData.code === 'MISSING_TOKEN') {
          console.warn('⚠️ Token inválido o expirado. Limpiando sesión...');
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          showSnackbar('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.', 'warning');
          // Recargar página para volver al login
          setTimeout(() => window.location.reload(), 2000);
          return;
        }

        showSnackbar(`Error al cargar registros: ${errorData.error || 'Error desconocido'}`, 'error');
      }
    } catch (error) {
      console.error('❌ Error al cargar registros:', error);
      showSnackbar('Error al cargar registros', 'error');
    }
  };

  const handleOpenModal = () => {
    setEditMode(false);
    setSelectedRegistro(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditMode(false);
    setSelectedRegistro(null);
    setSelectedFile(null);
    resetForm();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tamaño (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        showSnackbar('El archivo no debe superar los 10MB', 'error');
        return;
      }
      // Validar tipo
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        showSnackbar('Solo se permiten archivos PDF, DOC, DOCX, JPG y PNG', 'error');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async (registroId) => {
    if (!selectedFile) return;

    try {
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('document', selectedFile);

      const response = await fetch(`/api/registros-sanitarios/${registroId}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        showSnackbar('Documento subido exitosamente', 'success');
        setSelectedFile(null);
        loadRegistros();
        return true;
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error al subir el documento', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      showSnackbar('Error al subir el documento', 'error');
      return false;
    }
  };

  const handleDownloadDocument = async (registroId, documentName) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/registros-sanitarios/${registroId}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = documentName || 'registro-sanitario.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showSnackbar('Documento descargado exitosamente', 'success');
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error al descargar el documento', 'error');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      showSnackbar('Error al descargar el documento', 'error');
    }
  };

  const handleDeleteDocument = async (registroId) => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/registros-sanitarios/${registroId}/document`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showSnackbar('Documento eliminado exitosamente', 'success');
        // Actualizar el registro seleccionado para quitar el documento
        setSelectedRegistro(prev => ({
          ...prev,
          document_name: null,
          document_path: null
        }));
        loadRegistros();
        return true;
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Error al eliminar el documento', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showSnackbar('Error al eliminar el documento', 'error');
      return false;
    }
  };

  const handleView = async (registro) => {
    console.log('👁️ Abriendo vista de registro:', registro);
    setSelectedRegistro(registro);
    setOpenViewModal(true);
  };

  const handleEdit = async (registro) => {
    try {
      const token = localStorage.getItem('token');

      console.log('🔄 Cargando registro para editar:', registro.id);

      const response = await fetch(`/api/registros-sanitarios/${registro.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Registro cargado:', data);
        setFormData({
          regNo: data.numero_registro || '',
          fechaVencimiento: data.fecha_vencimiento || '',
          nombre: data.producto_nombre || '',
          principiosActivos: data.principios_activos || '',
          laboratorioFabricante: data.laboratorio_fabricante || '',
          para: data.indicacion || '',
          paisOrigen: data.pais_origen || '',
          distribuidoPor: data.distribuido_por || '',
          farmaceuticoResponsable: data.farmaceutico_responsable || '',
          emailFarmaceutico: data.farmaceutico_email || '',
          formaFarmaceutica: data.forma_farmaceutica || '',
          clasificacion: data.clasificacion_detallada || '',
          viaAdministracion: data.via_administracion || '',
          presentacion: data.presentaciones || '',
          vidaUtil: data.vida_util || '',
          productoControlado: data.producto_controlado === 1,
          enRenovacion: data.en_renovacion === 1,
          codigoSAP: data.codigo_sap || '',
        });
        setSelectedRegistro(data);
        setEditMode(true);
        setOpenModal(true);
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        showSnackbar(`Error al cargar el registro: ${errorData.error || 'Error desconocido'}`, 'error');
      }
    } catch (error) {
      console.error('❌ Error al cargar registro:', error);
      showSnackbar('Error al cargar el registro', 'error');
    }
  };

  const handleDeleteClick = (registro) => {
    console.log('🗑️ Abriendo diálogo de eliminación:', registro);
    setSelectedRegistro(registro);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/registros-sanitarios/${selectedRegistro.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        showSnackbar('Registro sanitario eliminado exitosamente', 'success');
        setOpenDeleteDialog(false);
        setSelectedRegistro(null);
        loadRegistros();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al eliminar el registro');
      }
    } catch (error) {
      console.error('Error al eliminar:', error);
      showSnackbar(error.message || 'Error al eliminar el registro sanitario', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      regNo: '',
      fechaVencimiento: '',
      nombre: '',
      principiosActivos: '',
      laboratorioFabricante: '',
      para: '',
      paisOrigen: '',
      distribuidoPor: '',
      farmaceuticoResponsable: '',
      emailFarmaceutico: '',
      formaFarmaceutica: '',
      clasificacion: '',
      viaAdministracion: '',
      presentacion: '',
      vidaUtil: '',
      productoControlado: false,
      enRenovacion: false,
      codigoSAP: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Validar campos requeridos - SOLO el nombre del producto es obligatorio
      if (!formData.nombre || formData.nombre.trim() === '') {
        showSnackbar('Por favor ingrese el nombre del producto', 'error');
        setLoading(false);
        return;
      }

      // Mapear campos del formulario a los nombres que espera el backend
      const requestBody = {
        numero_registro: formData.regNo,
        fecha_expedicion: formData.fechaVencimiento ? new Date().toISOString().split('T')[0] : null,
        fecha_vencimiento: formData.fechaVencimiento || null,
        nombre: formData.nombre || null,
        principios_activos: formData.principiosActivos || null,
        laboratorio_fabricante: formData.laboratorioFabricante || null,
        indicacion: formData.para || null,
        pais_origen: formData.paisOrigen || 'Guatemala',
        distribuido_por: formData.distribuidoPor || null,
        farmaceutico_responsable: formData.farmaceuticoResponsable || null,
        farmaceutico_email: formData.emailFarmaceutico || null,
        forma_farmaceutica: formData.formaFarmaceutica || null,
        clasificacion_detallada: formData.clasificacion || null,
        via_administracion: formData.viaAdministracion || null,
        presentaciones: formData.presentacion || null,
        vida_util: formData.vidaUtil || null,
        producto_controlado: formData.productoControlado || false,
        en_renovacion: formData.enRenovacion || false,
        codigo_sap: formData.codigoSAP || null,
        estado: editMode && selectedRegistro ? selectedRegistro.estado : 'vigente',
      };

      console.log('📤 Enviando registro sanitario:', requestBody);

      const url = editMode
        ? `/api/registros-sanitarios/${selectedRegistro.id}`
        : `/api/registros-sanitarios`;

      const method = editMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📥 Respuesta del servidor:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Registro guardado:', data);

        // Si hay un archivo seleccionado, subirlo
        if (selectedFile) {
          const uploadSuccess = await handleUploadDocument(data.id);
          if (uploadSuccess) {
            // Mostrar modal de éxito super cool
            setSuccessModalData({
              title: editMode ? '¡Registro Actualizado!' : '¡Registro Creado!',
              message: editMode
                ? `El registro sanitario "${formData.nombre}" y su documento han sido actualizados exitosamente.`
                : `El registro sanitario "${formData.nombre}" y su documento han sido creados exitosamente.`,
              type: editMode ? 'update' : 'create'
            });
            setShowSuccessModal(true);
          } else {
            showSnackbar(
              editMode ? 'Registro actualizado, pero error al subir documento' : 'Registro creado, pero error al subir documento',
              'warning'
            );
          }
        } else {
          // Mostrar modal de éxito super cool
          setSuccessModalData({
            title: editMode ? '¡Registro Actualizado!' : '¡Registro Creado!',
            message: editMode
              ? `El registro sanitario "${formData.nombre}" ha sido actualizado exitosamente.`
              : `El registro sanitario "${formData.nombre}" ha sido creado y está listo para usar.`,
            type: editMode ? 'update' : 'create'
          });
          setShowSuccessModal(true);
        }

        handleCloseModal();
        loadRegistros();
      } else {
        const errorData = await response.json();
        console.error('❌ Error del servidor:', errorData);
        showSnackbar(`Error: ${errorData.error || 'Error al guardar el registro'}`, 'error');
      }
    } catch (error) {
      console.error('❌ Error en handleSubmit:', error);
      showSnackbar(error.message || 'Error al crear el registro sanitario', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1); // Resetear a la primera página cuando se busca
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
          Registros Sanitarios
        </Typography>
        {canWrite() && (
          <PrimaryButton
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenModal}
          >
            Nuevo Registro
          </PrimaryButton>
        )}
      </Box>

      {/* Campo de Búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar por nombre, número de registro, principios activos, laboratorio..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <Search sx={{ color: '#94a3b8', mr: 1 }} />
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#f8fafc',
              '&:hover': {
                backgroundColor: '#f1f5f9',
              },
              '&.Mui-focused': {
                backgroundColor: '#ffffff',
              },
            },
          }}
        />
      </Box>

      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell>Reg. No.</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Laboratorio</TableCell>
              <TableCell>Fecha Vencimiento</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {registros.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 8, color: '#94a3b8' }}>
                  <Typography variant="body1">
                    No hay registros sanitarios. Crea uno nuevo para comenzar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              registros.map((registro) => (
                <TableRow key={registro.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#2196f3' }}>
                      {registro.numero_registro || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {registro.producto_nombre || registro.nombre || 'Sin especificar'}
                  </TableCell>
                  <TableCell>
                    {registro.laboratorio_fabricante || 'Sin especificar'}
                  </TableCell>
                  <TableCell>
                    {registro.fecha_vencimiento
                      ? new Date(registro.fecha_vencimiento).toLocaleDateString('es-GT', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })
                      : 'Sin especificar'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const statusInfo = registro.statusInfo;
                      if (!statusInfo) {
                        return (
                          <Chip
                            label="Vigente"
                            size="small"
                            color="success"
                            sx={{ borderRadius: '8px' }}
                          />
                        );
                      }

                      const colors = {
                        verde: { bg: '#22c55e', text: '#fff', label: 'Vigente' },
                        amarillo: { bg: '#eab308', text: '#fff', label: 'Próximo a Vencer' },
                        naranja: { bg: '#f97316', text: '#fff', label: 'Por Vencer' },
                        rojo: { bg: '#ef4444', text: '#fff', label: 'Vencido' },
                        rosado: { bg: '#ec4899', text: '#fff', label: 'En Renovación' }
                      };

                      const colorConfig = colors[statusInfo.color] || colors.verde;

                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: colorConfig.bg,
                              boxShadow: `0 0 8px ${colorConfig.bg}60`,
                            }}
                          />
                          <Chip
                            label={colorConfig.label}
                            size="small"
                            sx={{
                              bgcolor: colorConfig.bg,
                              color: colorConfig.text,
                              borderRadius: '8px',
                              fontWeight: 600,
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#64748b', ml: 1, whiteSpace: 'nowrap' }}>
                            {statusInfo.estado === 'vencido'
                              ? `Hace ${statusInfo.diasRestantes} días`
                              : statusInfo.estado === 'proximo_vencer' || statusInfo.estado === 'por_vencer'
                              ? `Vence en ${statusInfo.diasRestantes} días`
                              : statusInfo.estado === 'vigente'
                              ? `${statusInfo.diasRestantes} días restantes`
                              : statusInfo.estado === 'renovacion'
                              ? ''
                              : ''}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleView(registro)}
                      title="Ver detalles"
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                    {registro.document_name && (
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleDownloadDocument(registro.id, registro.document_name)}
                        title="Descargar documento"
                      >
                        <GetApp fontSize="small" />
                      </IconButton>
                    )}
                    {canWrite() && (
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(registro)}
                        title="Editar"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {canDelete() && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(registro)}
                        title="Eliminar"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* Controles de Paginación */}
      {pagination.pages > 1 && (
        <Stack
          spacing={2}
          sx={{
            mt: 3,
            mb: 2,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Pagination
            count={pagination.pages}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: '8px',
                fontWeight: 500,
              },
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Mostrando {registros.length} de {pagination.total} registros
          </Typography>
        </Stack>
      )}

      {/* Modal de Creación */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {editMode ? 'Editar Registro Sanitario' : 'Nuevo Registro Sanitario'}
          </Typography>
          <IconButton onClick={handleCloseModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Nombre del Producto - ÚNICO CAMPO OBLIGATORIO */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del Producto *"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                placeholder="Ingrese el nombre del producto"
                helperText="⚠️ Campo obligatorio - Este es el único campo requerido"
                error={!formData.nombre && formData.nombre !== ''}
              />
            </Grid>

            {/* Reg. No. - AHORA OPCIONAL */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reg. No. (Opcional)"
                name="regNo"
                value={formData.regNo}
                onChange={handleInputChange}
                placeholder="PF-XXXXX-XXXX"
                helperText="Opcional - Formato: PF-XXXXX-XXXX"
              />
            </Grid>

            {/* Fecha de Vencimiento */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de Vencimiento"
                name="fechaVencimiento"
                type="date"
                value={formData.fechaVencimiento}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Principios Activos */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Principios Activos"
                name="principiosActivos"
                value={formData.principiosActivos}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>

            {/* Laboratorio Fabricante */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Laboratorio Fabricante"
                name="laboratorioFabricante"
                value={formData.laboratorioFabricante}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Para */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Para"
                name="para"
                value={formData.para}
                onChange={handleInputChange}
                placeholder="Indicación terapéutica"
              />
            </Grid>

            {/* País de Origen */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="País de Origen"
                name="paisOrigen"
                value={formData.paisOrigen}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Distribuido Por */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Distribuido Por"
                name="distribuidoPor"
                value={formData.distribuidoPor}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Farmacéutico Responsable */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Farmacéutico Responsable"
                name="farmaceuticoResponsable"
                value={formData.farmaceuticoResponsable}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Email Farmacéutico */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email del Farmacéutico"
                name="emailFarmaceutico"
                type="email"
                value={formData.emailFarmaceutico}
                onChange={handleInputChange}
                placeholder="Para notificaciones"
              />
            </Grid>

            {/* Forma Farmacéutica */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Forma Farmacéutica"
                name="formaFarmaceutica"
                value={formData.formaFarmaceutica}
                onChange={handleInputChange}
                placeholder="Tabletas, jarabe, etc."
              />
            </Grid>

            {/* Clasificación */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Clasificación"
                name="clasificacion"
                value={formData.clasificacion}
                onChange={handleInputChange}
                placeholder="OTC, Prescription, etc."
              />
            </Grid>

            {/* Vía de Administración */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Vía de Administración</InputLabel>
                <Select
                  name="viaAdministracion"
                  value={formData.viaAdministracion}
                  onChange={handleInputChange}
                  label="Vía de Administración"
                >
                  <MenuItem value="oral">Oral</MenuItem>
                  <MenuItem value="intravenosa">Intravenosa</MenuItem>
                  <MenuItem value="intramuscular">Intramuscular</MenuItem>
                  <MenuItem value="subcutanea">Subcutánea</MenuItem>
                  <MenuItem value="topica">Tópica</MenuItem>
                  <MenuItem value="oftalmica">Oftálmica</MenuItem>
                  <MenuItem value="nasal">Nasal</MenuItem>
                  <MenuItem value="rectal">Rectal</MenuItem>
                  <MenuItem value="otra">Otra</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Vida Útil */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vida Útil"
                name="vidaUtil"
                value={formData.vidaUtil}
                onChange={handleInputChange}
                placeholder="24 meses, 3 años, etc."
              />
            </Grid>

            {/* Presentación y Envase */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Presentación y Envase"
                name="presentacion"
                value={formData.presentacion}
                onChange={handleInputChange}
                multiline
                rows={2}
                placeholder="Caja x 30 tabletas, frasco x 100ml, etc."
                helperText="Puede incluir múltiples presentaciones"
              />
            </Grid>

            {/* Código SAP */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código SAP"
                name="codigoSAP"
                value={formData.codigoSAP}
                onChange={handleInputChange}
                placeholder="Referencia SAP"
              />
            </Grid>

            {/* Producto Controlado */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.productoControlado}
                    onChange={handleInputChange}
                    name="productoControlado"
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Producto Controlado
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Requiere prescripción médica especial
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* En Renovación */}
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: `2px solid ${formData.enRenovacion ? '#ec4899' : '#e2e8f0'}`,
                  bgcolor: formData.enRenovacion ? '#fdf2f8' : 'transparent',
                  transition: 'all 0.2s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enRenovacion}
                      onChange={handleInputChange}
                      name="enRenovacion"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#ec4899' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ec4899' },
                      }}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Registro en Renovación
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          El estatus cambia a "En Renovación" (rosado) en el listado
                        </Typography>
                      </Box>
                      {formData.enRenovacion && (
                        <Chip
                          label="En Renovación"
                          size="small"
                          sx={{
                            bgcolor: '#ec4899',
                            color: '#fff',
                            fontWeight: 600,
                            borderRadius: '8px',
                            ml: 1,
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              </Box>
            </Grid>

            {/* Documento del Registro Sanitario */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#3b82f6' }}>
                📄 Documento del Registro Sanitario
              </Typography>
              <Box sx={{
                border: '2px dashed #cbd5e1',
                borderRadius: '12px',
                p: 3,
                textAlign: 'center',
                bgcolor: selectedFile ? 'rgba(59, 130, 246, 0.05)' : 'transparent',
                '&:hover': {
                  borderColor: '#3b82f6',
                  bgcolor: 'rgba(59, 130, 246, 0.02)',
                }
              }}>
                <input
                  accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                  style={{ display: 'none' }}
                  id="document-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="document-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUpload />}
                    sx={{ borderRadius: '12px', textTransform: 'none', mb: 1 }}
                  >
                    {selectedFile ? 'Cambiar Documento' : 'Seleccionar Documento'}
                  </Button>
                </label>

                {selectedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      icon={<Description />}
                      label={selectedFile.name}
                      onDelete={() => setSelectedFile(null)}
                      color="primary"
                      sx={{ borderRadius: '8px' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                )}

                {editMode && selectedRegistro?.document_name && !selectedFile && (
                  <Box sx={{ mt: 2 }}>
                    <Chip
                      icon={<Description />}
                      label={selectedRegistro.document_name}
                      onDelete={() => {
                        if (window.confirm('¿Estás seguro de que deseas eliminar este documento? Esta acción no se puede deshacer.')) {
                          handleDeleteDocument(selectedRegistro.id);
                        }
                      }}
                      color="success"
                      sx={{ borderRadius: '8px' }}
                    />
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                      Documento actual - Haz clic en la X para eliminar
                    </Typography>
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                  Formatos permitidos: PDF, DOC, DOCX, JPG, PNG (Máx. 10MB)
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleCloseModal}
            sx={{ borderRadius: '12px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <PrimaryButton
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Guardando...' : (editMode ? 'Actualizar Registro' : 'Guardar Registro')}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualización */}
      <Dialog
        open={openViewModal}
        onClose={() => setOpenViewModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Detalles del Registro Sanitario
          </Typography>
          <IconButton onClick={() => setOpenViewModal(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRegistro && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Número de Registro</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedRegistro.numero_registro || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Estado</Typography>
                {selectedRegistro.en_renovacion === 1 ? (
                  <Chip
                    label="En Renovación"
                    size="small"
                    sx={{ bgcolor: '#ec4899', color: '#fff', fontWeight: 600, borderRadius: '8px' }}
                  />
                ) : (
                  <Chip
                    label={selectedRegistro.estado === 'vigente' || !selectedRegistro.estado ? 'Vigente' : selectedRegistro.estado}
                    size="small"
                    color={selectedRegistro.estado === 'vigente' || !selectedRegistro.estado ? 'success' : 'default'}
                    sx={{ borderRadius: '8px' }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Nombre del Producto</Typography>
                <Typography variant="body1">{selectedRegistro.producto_nombre || 'Sin especificar'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Principios Activos</Typography>
                <Typography variant="body1">{selectedRegistro.principios_activos || 'Sin especificar'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Laboratorio Fabricante</Typography>
                <Typography variant="body1">{selectedRegistro.laboratorio_fabricante || 'Sin especificar'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">País de Origen</Typography>
                <Typography variant="body1">{selectedRegistro.pais_origen || 'Sin especificar'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Fecha de Vencimiento</Typography>
                <Typography variant="body1">
                  {selectedRegistro.fecha_vencimiento
                    ? new Date(selectedRegistro.fecha_vencimiento).toLocaleDateString('es-GT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })
                    : 'Sin especificar'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">Producto Controlado</Typography>
                <Typography variant="body1">
                  {selectedRegistro.producto_controlado === 1 ? 'Sí' : 'No'}
                </Typography>
              </Grid>
              {selectedRegistro.forma_farmaceutica && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Forma Farmacéutica</Typography>
                  <Typography variant="body1">{selectedRegistro.forma_farmaceutica}</Typography>
                </Grid>
              )}
              {selectedRegistro.via_administracion && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Vía de Administración</Typography>
                  <Typography variant="body1">{selectedRegistro.via_administracion}</Typography>
                </Grid>
              )}
              {selectedRegistro.presentaciones && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Presentación</Typography>
                  <Typography variant="body1">{selectedRegistro.presentaciones}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {selectedRegistro?.document_name && (
            <Button
              variant="contained"
              color="success"
              startIcon={<GetApp />}
              onClick={() => handleDownloadDocument(selectedRegistro.id, selectedRegistro.document_name)}
              sx={{ borderRadius: '12px', textTransform: 'none', mr: 'auto' }}
            >
              Descargar Documento
            </Button>
          )}
          <Button
            onClick={() => setOpenViewModal(false)}
            sx={{ borderRadius: '12px', textTransform: 'none' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Confirmar Eliminación
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            ¿Está seguro que desea eliminar el registro sanitario{' '}
            <strong>{selectedRegistro?.numero_registro}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{ borderRadius: '12px', textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            sx={{ borderRadius: '12px', textTransform: 'none' }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Modal de Éxito Super Cool */}
      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successModalData.title}
        message={successModalData.message}
        type={successModalData.type}
      />
    </Box>
  );
};

export default RegistrosSanitarios;
